import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, Check, X, Edit2, Save, BookOpen, UserMinus } from "lucide-react";

export default function Vereniging() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [editingVer, setEditingVer] = useState(false);
  const [verInput, setVerInput] = useState("");
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", notes: "" });
  const [routinesFor, setRoutinesFor] = useState(null); // { turner, items }

  const load = async () => {
    try {
      const meRes = await api.get("/vereniging/me");
      setMe(meRes.data);
      setVerInput(meRes.data.vereniging || "");
      if (meRes.data.vereniging) {
        const r = await api.get("/vereniging/members");
        setData(r.data);
        if (r.data.is_trainer) {
          const p = await api.get("/vereniging/pending");
          setPending(p.data);
        }
      }
    } catch (e) { setErr(formatError(e)); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const saveVereniging = async () => {
    try {
      await api.post("/vereniging/me", { vereniging: verInput });
      setEditingVer(false);
      load();
    } catch (e) { setErr(formatError(e)); }
  };

  const accept = async (id) => { await api.post(`/vereniging/accept/${id}`); load(); };
  const remove = async (id) => {
    if (!window.confirm("Lid verwijderen uit de vereniging?")) return;
    await api.post(`/vereniging/remove/${id}`); load();
  };
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({ name: m.name, notes: m.vereniging_notes || "" });
  };
  const saveEdit = async (id) => {
    await api.patch(`/vereniging/turner/${id}`, editForm);
    setEditingId(null);
    load();
  };
  const viewRoutines = async (m) => {
    const r = await api.get(`/vereniging/turner/${m.id}/routines`);
    setRoutinesFor({ turner: m, items: r.data });
  };

  if (!user) return <div className="text-muted-foreground">Log in om je vereniging te bekijken.</div>;
  if (!me) return <div className="text-muted-foreground">Laden...</div>;

  return (
    <div data-testid="vereniging-page">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Samenwerken</div>
      <h1 className="heading-display text-4xl sm:text-5xl mt-1">Vereniging</h1>
      <p className="text-slate-600 mt-3 mb-6">Trainers en turners van dezelfde vereniging in één overzicht.</p>

      {/* Eigen vereniging instellen */}
      <div className="gs-card p-5 mb-6" data-testid="vereniging-setting">
        {!me.vereniging || editingVer ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="text-sm font-medium text-slate-700">Mijn verenigingsnaam</label>
              <input value={verInput} onChange={(e) => setVerInput(e.target.value)} placeholder="bv. TV Voorwaarts" className="mt-1 w-full px-3 py-2 border border-input rounded-lg bg-card" data-testid="vereniging-input"/>
            </div>
            <button onClick={saveVereniging} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2" data-testid="vereniging-save"><Save size={14}/> Opslaan</button>
            {me.vereniging && <button onClick={() => { setEditingVer(false); setVerInput(me.vereniging); }} className="px-3 py-2 text-slate-600">Annuleren</button>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Mijn vereniging</div>
              <div className="font-bold text-lg flex items-center gap-2"><Users size={18} className="text-orange-500"/> {me.vereniging}</div>
              {me.vereniging_status === "pending" && (
                <div className="text-xs text-amber-700 mt-1">⏳ Wacht op goedkeuring door een trainer</div>
              )}
              {me.vereniging_status === "active" && (
                <div className="text-xs text-emerald-700 mt-1">✓ Actief lid · rol: {me.role === "trainer" ? "Trainer" : "Turner"}</div>
              )}
            </div>
            <button onClick={() => setEditingVer(true)} className="text-sm text-orange-600 hover:underline flex items-center gap-1" data-testid="vereniging-edit"><Edit2 size={14}/> Wijzigen</button>
          </div>
        )}
        {err && <div className="text-sm text-red-600 mt-2">{err}</div>}
      </div>

      {/* Pending requests (alleen trainer) */}
      {data?.is_trainer && pending.length > 0 && (
        <div className="gs-card p-5 mb-6 border-l-4 border-l-amber-400" data-testid="pending-list">
          <h2 className="font-bold mb-3 flex items-center gap-2"><span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">{pending.length}</span> Wachten op goedkeuring</h2>
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid={`pending-${p.id}`}>
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.email}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => accept(p.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1" data-testid={`accept-${p.id}`}><Check size={14}/> Accepteren</button>
                  <button onClick={() => remove(p.id)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1" data-testid={`reject-${p.id}`}><X size={14}/> Weigeren</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      {data && data.members.length > 0 && (
        <div className="gs-card p-5">
          <h2 className="font-bold mb-3">Leden ({data.members.length})</h2>
          <div className="grid gap-3">
            {data.members.map((m) => (
              <div key={m.id} className="border border-border rounded-lg p-4" data-testid={`member-${m.id}`}>
                {editingId === m.id ? (
                  <div className="space-y-2">
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 border border-input rounded bg-card font-semibold" data-testid={`edit-name-${m.id}`}/>
                    <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Notitie (bv. niveau, opmerkingen)" rows={2} className="w-full px-3 py-2 border border-input rounded bg-card text-sm" data-testid={`edit-notes-${m.id}`}/>
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(m.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm" data-testid={`save-edit-${m.id}`}>Opslaan</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-slate-600 text-sm">Annuleren</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{m.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${m.role === "trainer" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}`}>{m.role === "trainer" ? "Trainer" : "Turner"}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                      {m.vereniging_notes && <div className="text-sm text-slate-700 mt-1.5 italic">📝 {m.vereniging_notes}</div>}
                    </div>
                    {data.is_trainer && m.role === "turner" && m.id !== user.id && (
                      <div className="flex gap-1.5">
                        <button onClick={() => viewRoutines(m)} title="Bekijk sprongen" className="p-2 hover:bg-slate-100 rounded" data-testid={`routines-${m.id}`}><BookOpen size={15}/></button>
                        <button onClick={() => startEdit(m)} title="Aanpassen" className="p-2 hover:bg-slate-100 rounded" data-testid={`edit-${m.id}`}><Edit2 size={15}/></button>
                        <button onClick={() => remove(m.id)} title="Uit vereniging verwijderen" className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`remove-${m.id}`}><UserMinus size={15}/></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.members.length === 0 && (
        <div className="gs-card p-5 text-center text-muted-foreground">Nog geen actieve leden. Wanneer turners zich registreren met dezelfde verenigingsnaam, kun je ze hier goedkeuren.</div>
      )}

      {/* Routines modal */}
      {routinesFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRoutinesFor(null)}>
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="routines-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Sprongen van {routinesFor.turner.name}</h3>
              <button onClick={() => setRoutinesFor(null)} className="text-slate-500 hover:text-slate-800"><X size={20}/></button>
            </div>
            {routinesFor.items.length === 0 ? (
              <div className="text-muted-foreground text-sm">Nog geen sprongen opgeslagen.</div>
            ) : (
              <div className="space-y-2">
                {routinesFor.items.map((r) => (
                  <div key={r.id} className="border border-border rounded p-3 text-sm">
                    <div className="font-semibold">{r.titel}</div>
                    <div className="text-xs text-muted-foreground mb-1">{r.onderdeel} · {r.leeftijd} · {r.niveau} · {r.toestel} · {new Date(r.created_at).toLocaleDateString()}</div>
                    <div className="text-xs">{(r.sprongen || []).map((s) => s.naam).join(" → ")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
